import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import {
  CurrencyAcquisitionBreakdown,
  CurrencyAcquisitionBucket,
  CurrencyAcquisitionSource,
  FinancialResults,
} from '../../models/financial-results';
import { FinancialService } from '../../services/financial.service';

interface CurrencyAcquisitionRow {
  date: string;
  coin: CurrencyAcquisitionBucket;
  gem: CurrencyAcquisitionBucket;
}

@Component({
  selector: 'app-financial-acquisition',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerModule, TableModule, DatePipe, DecimalPipe],
  templateUrl: './financial-acquisition.component.html',
  styleUrls: ['./financial-acquisition.component.scss'],
})
export class FinancialAcquisitionComponent implements OnInit {
  rows: CurrencyAcquisitionRow[] = [];
  summary: CurrencyAcquisitionBreakdown = this.emptyBreakdown();
  isLoading = false;
  errorMessage = '';
  since!: Date;
  until!: Date;

  constructor(
    private financialService: FinancialService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    const { since, until } = this.getDefaultRange();
    this.applyRange(since, until);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
      const since = params['since'] ? this.parseDate(params['since']) : defaultSince;
      const until = params['until'] ? this.parseDate(params['until']) : defaultUntil;

      this.applyRange(since, until);
      this.fetchResults();
    });
  }

  applyDateFilter(): void {
    const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
    const since = this.since ? this.startOfDay(this.since) : defaultSince;
    const until = this.until ? this.startOfDay(this.until) : defaultUntil;
    this.applyRange(since, until);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        since: this.formatDateForApi(this.since),
        until: this.formatDateForApi(this.until),
      },
    });
  }

  refresh(): void {
    this.fetchResults();
  }

  exportToCsv(): void {
    if (!this.rows.length) {
      return;
    }

    const headers = [
      'Data',
      'Moedas adquiridas',
      'Origens de moedas',
      'Gemas adquiridas',
      'Origens de gemas',
    ];
    const csvRows = this.rows.map((row) => [
      this.formatDateForDisplay(row.date),
      row.coin.total,
      this.formatSources(row.coin.sources),
      row.gem.total,
      this.formatSources(row.gem.sources),
    ]);
    const csv = [headers, ...csvRows]
      .map((line) => line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `aquisicao_moedas_gemas_${this.formatDateForApi(this.since)}_a_${this.formatDateForApi(this.until)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  fetchResults(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.financialService
      .getFinancialResults({
        since: this.formatDateForApi(this.since),
        until: this.formatDateForApi(this.until),
      })
      .subscribe({
        next: (response) => {
          this.summary = this.normalizeBreakdown(response?.currency_acquisition_summary);
          this.rows = (response?.results ?? []).map((row) => this.buildRow(row));
          this.isLoading = false;
        },
        error: () => {
          this.rows = [];
          this.summary = this.emptyBreakdown();
          this.errorMessage = 'Não foi possível carregar a aquisição de moedas e gemas.';
          this.isLoading = false;
        },
      });
  }

  trackSource(index: number, source: CurrencyAcquisitionSource): string {
    return `${index}-${source.type}`;
  }

  private buildRow(row: FinancialResults): CurrencyAcquisitionRow {
    const breakdown = this.normalizeBreakdown(row.currency_acquisition);
    return {
      date: row.date,
      coin: breakdown.coin,
      gem: breakdown.gem,
    };
  }

  private normalizeBreakdown(breakdown?: CurrencyAcquisitionBreakdown): CurrencyAcquisitionBreakdown {
    return {
      coin: this.normalizeBucket(breakdown?.coin),
      gem: this.normalizeBucket(breakdown?.gem),
    };
  }

  private normalizeBucket(bucket?: CurrencyAcquisitionBucket): CurrencyAcquisitionBucket {
    const sources = (bucket?.sources ?? [])
      .map((source) => ({
        type: String(source.type || ''),
        label: String(source.label || source.type || 'Origem não informada'),
        amount: Number(source.amount || 0),
        transactions_count: Number(source.transactions_count || 0),
      }))
      .filter((source) => source.amount > 0)
      .sort((a, b) => b.amount - a.amount);

    return {
      total: Number(bucket?.total || 0),
      sources,
    };
  }

  private emptyBreakdown(): CurrencyAcquisitionBreakdown {
    return {
      coin: { total: 0, sources: [] },
      gem: { total: 0, sources: [] },
    };
  }

  private formatSources(sources: CurrencyAcquisitionSource[]): string {
    return sources
      .map((source) => `${source.label}: ${source.amount} (${source.transactions_count})`)
      .join(' | ');
  }

  private getDefaultRange(): { since: Date; until: Date } {
    const until = this.startOfDay(new Date());
    const since = this.addDays(until, -29);
    return { since, until };
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return this.startOfDay(result);
  }

  private parseDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return this.startOfDay(new Date());
    }
    return new Date(year, month - 1, day);
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private applyRange(since: Date, until: Date): void {
    const normalizedSince = this.startOfDay(since);
    const normalizedUntil = this.startOfDay(until);
    this.since = normalizedSince <= normalizedUntil ? normalizedSince : normalizedUntil;
    this.until = normalizedSince <= normalizedUntil ? normalizedUntil : normalizedSince;
  }

  private formatDateForDisplay(value: string): string {
    const [year, month, day] = value.split('-');
    if (!year || !month || !day) {
      return value;
    }
    return `${day}/${month}/${year}`;
  }
}
